/**
 * The object UI's contract with the platform: the ONE place the frontend
 * states the shapes the declarations and owning modules serve, as
 * docs/features/object-ui-and-skills/03_platform_contract_spec.md §4 defines
 * them: the shapes owning modules build in `modules/object_contract.py`, the
 * presented steps, `cis_field` and the manifest in
 * `apps/api/app/object_contract.py`.
 *
 * Every other object-UI file imports from here. When the generated manifest
 * (`data/object-manifest.generated.ts`) and the OpenAPI types for these shapes
 * land, this file becomes aliases over them, as `api-types.ts` is over
 * `api-schema.generated.ts`.
 */
import type { FeatureName } from "../../config/features";

// ── Shared ───────────────────────────────────────────────────────────────

/** A qualified object id: `orders.order`. */
export type ObjectId = string;
/** A qualified command id, which is the action id: `orders.release_order_lines`. */
export type CommandId = string;

/** A link to another record, named, never a URL: the shell resolves it through the manifest. */
export interface ObjectRef {
  object: ObjectId;
  id: string;
}

/** One starting filter of an entity picker as a choice: its list param, its words, and the values to choose from. */
export interface ListFilterChoice {
  param: string;
  label: string;
  options: { value: string; label: string }[];
}

/** The canonical field error (`api-validation.md`): a dotted request-body path. */
export interface FieldError {
  field: string;
  code: string;
  message: string;
}

/** A JSON Schema node as OpenAPI serves it, `$ref`s resolved. Only what the widget set reads is typed. */
export interface JsonSchema {
  type?: string | string[];
  title?: string;
  description?: string;
  format?: string;
  enum?: (string | number)[];
  default?: unknown;
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  /** A discriminated union's tag on each of its branches. */
  const?: unknown;
  readOnly?: boolean;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  items?: JsonSchema;
  anyOf?: JsonSchema[];
  oneOf?: JsonSchema[];
  /** Validation keywords the generated manifest carries through from pydantic; the shell's dry run enforces them. */
  additionalProperties?: boolean | JsonSchema;
  /** A map's keys by pattern (a dictionary of codes), and how many it may hold. */
  patternProperties?: Record<string, JsonSchema>;
  maxProperties?: number;
  /** A discriminated union: the property whose `const` names the branch. */
  discriminator?: { propertyName: string; mapping?: Record<string, string> };
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  minItems?: number;
  maxItems?: number;
  contentMediaType?: string;
  "x-cis-field"?: CisField;
}

/** `cis_field`'s widget hint (platform spec §4.5). */
export type Widget =
  | "text"
  | "number"
  | "money"
  | "date"
  | "textarea"
  | "select"
  | "radio"
  | "checkbox"
  | "suggest"
  | "entity"
  | "address"
  | "file"
  | "table"
  | "state"
  | "identifier"
  | "link"
  | "formatted_text";

/** The one `x-cis-field` object on a schema field (platform spec §4.5). */
export interface CisField {
  widget?: Widget;
  /** Entity picker (or address): the object whose list is searched; its collection command is the nested create. */
  entity?: ObjectId;
  /**
   * With `entity`: the list column to send instead of the picked row's key (Set contact… picks a customer user, keyed
   * by `link_id`, and sends `person_id`).
   */
  entity_value?: string;
  /** Free text with suggestions from this object's list `q`. */
  suggestions?: ObjectId;
  enum_labels?: Record<string, string>;
  /** On a table row field: the sibling row field holding that row's maximum. */
  max_from?: string;
  /** On a table field: rows are ticked in or out; unticked rows are not sent. */
  selectable?: boolean;
  /**
   * On a table field: the person adds and removes the rows (Request merchandise quote's request lines). The label
   * of the control that adds a row, which starts from the row's defaults.
   */
  add_rows?: string;
  /**
   * The field must be filled although its schema default keeps the dry run answering while it is empty: the shell
   * does not label it optional.
   */
  required?: boolean;
  /** On a table row field: the label of its "set to max" action. */
  fill_label?: string;
  /** Shown only while those sibling fields have those values: one value, or a list of which any will do. */
  show_if?: Record<string, ShowIfValue | readonly ShowIfValue[]>;
  min_date?: "today";
  /** On a ListQuery's `sort`: the sortable item fields. */
  sort_fields?: string[];
  /** The web shell's registered input widget for this field (the only field that may carry `widget_context`). */
  custom_widget?: string;
  /**
   * Never drawn as a field. On a table row field: the row's key, kept in the body sent back but not shown as a column.
   * On a top-level request field: a dry-run choice's key, so the shell draws nothing for it before the first dry run.
   */
  hidden?: boolean;
  /**
   * On a ListQuery field of a list that also holds a second object: the filter only makes sense for the list's own
   * rows, so setting it leaves the second object's rows out (a One order's route).
   */
  own_kind_only?: boolean;
  /** The opposite: the filter narrows only the second object's rows (Tharstern orders' Stock call-offs). */
  included_kind_only?: boolean;
  /** A file picker that uploads first: the route, its file part, and the answer's key holding the ref the field takes. */
  upload?: UploadTarget;
  /** The media types an upload field takes (`application/pdf`); the route still judges the bytes. */
  accept?: string[];
  /** The largest file an upload field sends, in bytes. */
  max_bytes?: number;
  /**
   * On a list of files of the command's own (multipart): the hidden field carrying each file's key, in the same order.
   * The dry run says which keys the record takes as `FieldHint.file_slots`.
   */
  keyed_by?: string;
  /** A fact or column whose value names a record of this object (an order number, a line reference). */
  ref?: ObjectId | ObjectId[];
  /** Where the number to link sits when this field shows a name (see `ManifestColumn.ref_id_field`). */
  ref_id_field?: string;
}

export type ShowIfValue = string | number | boolean;

// ── Manifest (build-time declarations; platform spec §4.6) ───────────────

export interface Identifier {
  kind: "cis_number" | "field" | "uuid";
  /** Every kind: the list/record field holding it, which fills the record route's param. */
  field: string;
  prefix?: string | null;
  /** cis_number: `cis_numbers.cis_number_pattern(prefix)`, never restated here. */
  pattern?: string | null;
  /** cis_number: `cis_numbers.cis_line_ref_pattern(prefix)`; the record's number is the text before the last ".". */
  line_pattern?: string | null;
}

export interface ManifestQueue {
  id: string;
  label: string;
  params: Record<string, string | number | boolean | string[]>;
  count: boolean;
  /** The list opens on this queue unless the link names a queue, filters or a search ("All" is a click away). */
  default?: boolean;
}

export type ValueKind =
  | "text"
  | "number"
  | "money"
  | "date"
  | "datetime"
  | "state"
  | "identifier"
  | "boolean"
  /** An absolute http(s) URL, opened in a new tab (`linkHref`). */
  | "link"
  /** A `FileValue`, or a list of them: each file's name, opened with the person's sign-in (`filesOf`). Never a sort. */
  | "file"
  /** Simple Markdown, stored and carried as written; drawn from `parseFormattedText`'s blocks. */
  | "formatted_text";

/**
 * A file a record fact or table column holds (`modules/object_contract.py` `FileValue`): its name, the API path that
 * serves it behind the person's sign-in (never a public URL), and its size where the module knows it.
 */
export interface FileValue {
  name: string;
  href: string;
  size_bytes: number | null;
}

/** A column, fact or child column, derived from a titled schema property. */
export interface ManifestColumn {
  field: string;
  label: string;
  kind: ValueKind;
  /** (value → label) a state column's field declares; absent where it declares none. */
  enum_labels?: Record<string, string>;
  /**
   * The object this field's value names (`cis_field(ref=...)`). Each value, one or a list, links to that object's
   * record; a line reference (ORD-K48193.03) opens the record it is a line of. A list of objects: each value links to
   * the first whose number pattern or line pattern it matches, and shows as text when it matches none.
   */
  ref?: ObjectId | ObjectId[];
  /**
   * Set where this field shows a name (a customer's) and the link needs the number: the value of the field it names,
   * a sibling field of the same record or row, or a dotted path into a nested object (`supplier.supplier_no`).
   */
  ref_id_field?: string;
  /** A `link` column only (`cis_field(link_text=...)`): the words shown in place of the URL ("View portal"). */
  link_text?: string;
  /**
   * The values are one chain, each a step from the last, drawn joined by › rather than as a list (`cis_field(path=True)`):
   * what a catalogue item is based on, Dictionary › Business card › Core range › Business card. Absent where false.
   */
  path?: true;
  /**
   * Set where one field names records of several objects with nothing in the value to say which (a mailbox tag: an
   * order number, a supplier, a purchase invoice's uuid): a sibling field of the same record or row holding, value for
   * value, the object id each names (one, or a list in step with this field). Each value links to that object's
   * record; in a `path`, a step with an object but no id links that object's list (the Dictionary starting a
   * catalogue item's Based on); an empty string or an object outside `ref` links nothing.
   */
  ref_object_field?: string;
  /**
   * A second field of the same record or row, shown on hover and keyboard focus over this one
   * (`cis_field(detail=...)`): the VAT rate behind a line's VAT amount.
   */
  detail?: ManifestColumn | null;
  /**
   * A list or child-table column only (`cis_field(fallback=...)`): the other field of the same row this column
   * shows while its own value is null or empty, drawn by that field's own kind (`shownColumnOf`). The label, sort
   * and filter stay this column's.
   */
  fallback?: ManifestColumn | null;
  /**
   * Text written or influenced by people outside the business (`cis_field(untrusted=True)`): an email's sender,
   * subject and text, its files' names. Claude's tools fence each value as untrusted data; One shows it as usual.
   * Absent where false.
   */
  untrusted?: true;
}

/** Record facts drawn together on a line of their own (`record.fact_groups`); each is still in `facts`. */
export interface ManifestFactGroup {
  label?: string | null;
  fields: string[];
  /**
   * The section the group belongs to: groups naming the same section draw together under that heading, after the
   * other facts, each on its own line, with a long text drawn in place (an email's text under its From and To).
   */
  section?: string | null;
  /** Leave the section's heading out (the record's main content, an email's own text): any group of it may say so. */
  section_heading?: boolean;
  /** "title": the group's facts drawn as one bold line with no labels (an email's subject above its text). */
  style?: "title" | null;
}

export interface ManifestListFilter {
  param: string;
  label: string;
  kind:
    "enum" | "multi_enum" | "boolean" | "text" | "date" | "number" | "entity";
  /** (value, label) for enums. */
  options: [string, string][];
  /**
   * An entity filter only: the object whose record the value names. The list draws the command dialog's name
   * picker over that object's list; the value is the picked row's list key, or the column `entity_value` names.
   */
  object?: ObjectId | null;
  /** An entity filter only: the column of the named object's list rows the value is, where it is not the key. */
  entity_value?: string | null;
  /**
   * An entity filter only: the filter holds several records (a repeated query param). The list draws the picker with
   * each pick kept as a removable chip, and the link repeats the param.
   */
  multiple?: boolean;
  /** Which filter to use and what it takes; Claude reads it, the staff list does not show it. */
  description?: string | null;
  /**
   * The value the list applies while the param is absent, where the filter has one (a switch that is on until staff
   * turn it off). The screen shows the filter at this value; choosing it again clears the param.
   */
  default?: string | boolean | null;
  /** On a list that holds a second object: setting this filter leaves that object's rows out. */
  own_kind_only?: boolean;
  /** On a list that holds a second object: this filter narrows only that object's rows. */
  included_kind_only?: boolean;
  /**
   * An enum filter only: the value a list filter's name picker over this list sets it to, so the filter can name a
   * record the list leaves out by default (`all` on the Users list's state: a user who has left). A command field's
   * picker leaves the filter at its default.
   */
  picker_value?: string | null;
}

/**
 * The one second object a list also holds (concepts.yaml `list_includes`, the contract's contained exception to "one
 * object per list"): a row whose `kind` is `kind` is a record of `object`, any other of the list's own object.
 */
export interface ManifestListIncludes {
  object: ObjectId;
  kind: string;
  own_kind: string;
}

export interface ManifestList {
  path: string;
  /** = identifier.field. */
  key_field: string;
  /** = record.title_field; on the list item too. */
  title_field: string;
  columns: ManifestColumn[];
  filters: ManifestListFilter[];
  /** (field, label). */
  sorts: [string, string][];
  searchable: boolean;
  queues: ManifestQueue[];
  /**
   * Params the list route accepts that are no filter of One's or Claude's. A command field's picker may fix one as its
   * `list_scope`; present only on the lists that have any.
   */
  hidden_params?: string[];
  /** Present only on the lists that hold a second object. */
  includes?: ManifestListIncludes | null;
}

export interface ManifestRecord {
  path: string;
  path_param: string;
  title_field: string;
  /** At most two (ObjectHeader's rule). */
  state_fields: string[];
  /** (state field → value → label) for the state fields that declare labels. */
  state_labels?: Record<string, Record<string, string>>;
  facts: ManifestColumn[];
  /** Facts drawn together on a line of their own, in this order; absent where the object groups none. */
  fact_groups?: ManifestFactGroup[];
  /** The record's timeline route (`Paginated[TimelineEvent]`), where one exists. */
  timeline_path: string | null;
  /**
   * A POST route the record page calls once each time a person opens the
   * record, with `{ [path_param]: key }` as its JSON body (a ticket's
   * first-opened stamp). Fire-and-forget; Claude's tools never call it.
   */
  on_open: string | null;
  /**
   * `?include=` tokens for the children that cost the server a call to another system, so the record leaves them
   * empty unless asked (`ChildDecl.include`). The shell asks for all of them, because it shows all of them.
   */
  include: string[];
  /**
   * What the timeline's search covers, worded for the person (shown under the
   * search box); null where the timeline takes no search.
   */
  timeline_search?: string | null;
  /** The timeline's own filters (kind, a date range), as a list's. */
  timeline_filters?: ManifestListFilter[];
}

/**
 * A child's rows drawn as a grid, one value per cell, instead of a table (`children[].grid`): a row of the grid for
 * each `row_field` value, a column for each `column_field` value, the `cell_field` value where they cross and the
 * row's own commands in that cell. Every field is a field of the child's rows; `cell_field` and `column_field` are
 * also columns of it, whose kinds format what is drawn. The owning module words and marks what the shell would
 * otherwise compare: a row with no `row_label_field` is left out, `state_field` holds the cell's text beside (or in
 * place of) its value, and `highlight_field` the boolean that marks it.
 */
export interface ManifestChildGrid {
  row_field: string;
  row_label_field: string;
  column_field: string;
  cell_field: string;
  /** One small grid for each value of it. */
  group_field?: string;
  state_field?: string;
  highlight_field?: string;
}

/**
 * A child's rows drawn as stacked blocks, one under another, instead of a table (`children[].stack`), for rows that are
 * mostly prose a person reads before acting (an email's proposed actions). Each block's heading is the row's
 * `heading_fields` (columns of the rows, linked where a column's `ref` names a record), its state (the child's state
 * column) and its commands. Under it, each of `lines` in full, nothing behind a hover or a disclosure: a line's label,
 * its text (`field`) and its points as a list (`items_field`: each a string, or `{text, note}` with the note, such as
 * where the point came from, beside it); a line with neither is left out. A row whose state is one of `folded_states`
 * shows its heading only, with `folded_detail_field` beside the state, and the lines marked `when_folded` behind one
 * "Why" toggle (an action One ran on its own keeps its reasons a click away).
 */
export interface ManifestChildStack {
  heading_fields: string[];
  lines: {
    label: string | null;
    field?: string;
    items_field?: string;
    when_folded?: boolean;
  }[];
  folded_states?: string[];
  folded_detail_field?: string;
}

export interface ManifestChild {
  object: ObjectId;
  /** The child object's plural. */
  label: string;
  /** The record-view field holding the rows (each may carry `commands`). */
  field: string;
  key_field: string;
  columns: ManifestColumn[];
  /**
   * The field is `ChildRows` (the first rows and their total), not every row:
   * the rest are the child's list filtered by `parent_filter`.
   */
  paged?: boolean;
  /**
   * The child list's filter that selects this record's rows (`customer_id`):
   * its value is the record's field of that name, else the record's key.
   */
  parent_filter?: string | null;
  /** The object whose record each row opens, when that is not the child object; the row's key is its identifier. */
  opens?: ObjectId | null;
  /**
   * The row field that heads the row's commands (".01 → Acme Ltd, Exeter"), for a child whose key is not something a
   * person reads; absent where a row command is headed by the row's key.
   */
  title_field?: string;
  /**
   * (child column → the record field holding its total over every row), drawn as a Total row under the table. The
   * figure is the owning module's; the shell never adds up rows. Absent where the child totals nothing.
   */
  totals?: Record<string, ManifestColumn>;
  /** Draw the rows as a grid instead of a table; absent where the child is a table. */
  grid?: ManifestChildGrid;
  /** Draw the rows as stacked blocks instead of a table; absent where the child is a table. */
  stack?: ManifestChildStack;
  /** One quiet line under the table's heading, for what the rows do not say ("Invites last 30 days."); absent where there is none. */
  note?: string;
}

export interface ManifestRelated {
  object: ObjectId;
  /** Its plural. */
  label: string;
  /** List params templated from record fields: `{order_no: "{order_no}"}`. */
  params: Record<string, string>;
  /**
   * A cross-reference consulted now and then rather than worked through (an order's emails, an alert's tickets): drawn
   * as a link with its count on the record's "See also" line, not as a section of rows. Absent: a section (the default).
   * Declared beside the related list in concepts.yaml (`secondary: true`); ruled by Ben, 30 Oct 2026.
   */
  secondary?: boolean;
}

export interface ManifestStep {
  id: string;
  label: string;
  /** The module that declares the step and reports its status. */
  module: string;
  description: string;
}

/**
 * The tone of a state's badge, by what the state means (concepts.yaml `state_tones`): neutral (not started, a draft),
 * in_progress (waiting on someone, under way), attention (someone needs to act), done (done, accepted) or stopped
 * (declined, failed, cancelled). The shell picks the colour; the badge keeps its word.
 */
export type StateTone =
  "neutral" | "in_progress" | "attention" | "done" | "stopped";

export interface ManifestObject {
  id: ObjectId;
  module: string;
  label: string;
  plural: string;
  description: string;
  identifier: Identifier;
  states: string[];
  /** Null: child-only. */
  list: ManifestList | null;
  /** Null: list-only. */
  record: ManifestRecord | null;
  /** The objects whose records show this one's rows; empty for a top-level object. */
  parents: ObjectId[];
  children: ManifestChild[];
  related: ManifestRelated[];
  lifecycle: ManifestStep[];
  /** What the object is and how it comes about, for a person and their Claude; only where concepts.yaml writes it. */
  about?: string;
  /** State value → what the state means and what will happen; only where concepts.yaml writes them. */
  state_meanings?: Record<string, string>;
  /** State value → the tone of its badge; only where concepts.yaml writes them. A value with none is neutral. */
  state_tones?: Record<string, StateTone>;
  commands: CommandId[];
  /** The features its list and record routes need: One hides the object unless all are on for the person. */
  features: FeatureName[];
  /**
   * "withheld": Claude's tools neither describe, find, list nor read the object (concepts.yaml `claude: withheld`);
   * One shows it as usual. Absent where the object is exposed.
   */
  claude?: "exposed" | "withheld";
  /** Why the object is withheld from Claude; absent where it is not. */
  withheld_reason?: string;
}

export interface ManifestCommand {
  id: CommandId;
  module: string;
  label: string;
  description: string;
  /** An object id, a child object id, or `collection:<object id>`. */
  target: string;
  destructive: boolean;
  bulk: boolean;
  /** Its dry run calls an outside system. */
  external_calls: boolean;
  claude: "exposed" | "withheld";
  withheld_reason: string | null;
  method: "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  dry_run_path: string;
  content_type: "json" | "multipart";
  request_schema: JsonSchema | null;
  /**
   * The record facts this command sets, shown as settings beside them instead of a header button. With `value`, the
   * command turns an on/off fact to it (one of a pair); a command whose one field is a boolean is a switch on its own;
   * otherwise it changes the fact behind an edit button.
   */
  edits?: { fields: string[]; value?: boolean } | null;
  /** The target object's main command (`main: true`): a row's one button while it can run, the header's primary. */
  main?: boolean;
  /** What the command does when an email's proposed action runs it (the three-way rule); absent where undeclared. */
  proposal_class?: "asks_internal" | "asks_external" | "records" | "decides";
  /** For Claude, it takes on the destructiveness of the command it runs (Confirm action); absent otherwise. */
  inherits_destructive?: boolean;
  /** Only an email's proposed action runs it (its request carries a staged document): no list or Claude tool offers it. */
  proposal_only?: boolean;
}

/** An object an area holds; `sidebar: false` keeps it out of the sidebar (Claude still sees it in the area). */
export interface ManifestAreaObject {
  id: ObjectId;
  sidebar: boolean;
}

/**
 * An area: a group of related objects, the same as a heading in One's
 * sidebar (modules/concepts_areas.yaml). Insights holds MCP tools, not objects.
 */
export interface ManifestArea {
  id: string;
  label: string;
  /** One sentence for Claude on what the area holds. */
  description: string;
  objects: ManifestAreaObject[];
  tools: string[];
}

export interface ObjectManifest {
  /** How One fits together: the hand-written overview (modules/concepts_overview.md). */
  overview: string;
  /** One's areas, in sidebar order. */
  areas: ManifestArea[];
  objects: ManifestObject[];
  commands: ManifestCommand[];
}

// ── Runtime: record views (platform spec §4.2–§4.3) ──────────────────────

/** A command as a record view, a child row or `GET /objects/{id}/commands` serves it. */
export interface Command {
  name: CommandId;
  label: string;
  enabled: boolean;
  reason: string | null;
  destructive: boolean;
  href: string;
  dry_run_href: string;
  /**
   * The owner's word on whether this command leads its list (Ben's ruling 21): absent when the owner says nothing.
   * Once any command in a list carries it, only the `true` one leads, or none does (`ownerHasSpoken`).
   */
  main?: boolean | null;
}

export type StepState = "done" | "in_progress" | "to_do";
export type StepProblem = "late" | "at_risk";

export interface StepDetailItem {
  id: string;
  label: string;
  name: string;
  state: StepState;
  problem: StepProblem | null;
  status: string;
  link: ObjectRef | null;
}

export interface LifecycleStep {
  step: string;
  label: string;
  state: StepState;
  problem: StepProblem | null;
  status_lines: string[];
  alert: string | null;
  started_on: string | null;
  due_on: string | null;
  done_on: string | null;
  link: ObjectRef | null;
  detail: StepDetailItem[];
  info: string | null;
}

/**
 * A fact that changes what the record's main command will do, said above the header's buttons without blocking them:
 * "Solopress is on hold: you can release it, but the bill won't post until the hold is lifted." Amber for a warning,
 * plain for information. A record may carry several; a command that cannot run is not a notice (it is the header's
 * blocked line, with its reason).
 */
export interface RecordNotice {
  tone: "warn" | "info";
  text: string;
  /** The record the notice is about (the merch team's ticket), linked after the sentence; absent when it names none. */
  link?: RecordNoticeLink;
}

/** The record a notice names: the link's words and the record it opens. */
export interface RecordNoticeLink {
  label: string;
  record: ObjectRef;
}

/** A record view: the object's own fields plus the standard `commands`, `steps` and `notices`. */
export interface RecordView {
  commands: Command[];
  steps?: LifecycleStep[];
  notices?: RecordNotice[];
  [field: string]: unknown;
}

/** A list item or child row: its own fields, plus the commands that act on a child row. */
export interface ChildRow {
  commands?: Command[];
  [field: string]: unknown;
}

/** A paged child table on a record view (`ManifestChild.paged`): its first rows and how many there are. */
export interface ChildRows {
  rows: ChildRow[];
  total: number;
}

/** One event on a record's timeline (platform spec §4.7). */
export interface TimelineEvent {
  at: string;
  summary: string;
  actor: string | null;
  /**
   * Files or pages the event carries, in order. An href starting `/` is an
   * API path the shell fetches with the person's sign-in; any other is opened
   * as it stands.
   */
  links?: CommandLink[];
  /**
   * The record the event opens, when it is one (a shared communication on a
   * customer's timeline): the summary links to its page.
   */
  record?: ObjectRef | null;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ── Runtime: dry run and commit (platform spec §4.4) ─────────────────────

export type CheckState = "pass" | "warn" | "fail";

export interface Check {
  state: CheckState;
  label: string;
  evidence: string | null;
  link: ObjectRef | null;
  /** A failed check saying the command does not apply here (hide or block): its availability hides it. */
  not_applicable?: boolean;
}

export interface ChoiceOption {
  value: string;
  label: string;
  facts: string | null;
  is_default: boolean;
  why: string | null;
  /**
   * What stops the option being picked, shown on its greyed card. In a multiple choice a disabled option that is also
   * a default is ticked and locked on (a document that cannot be taken out of its own group): it stays in the pick.
   */
  disabled_reason: string | null;
  /** A small label drawn on the card ("Matched now", "Suggested"): what the option is, not a heading over a group. */
  tag?: string | null;
}

export interface Choice {
  /** The request-body path the chosen value is sent in. */
  key: string;
  label: string;
  options: ChoiceOption[];
  /** Several may be picked: the body path takes a list of values, and any number of options may be defaults. */
  multiple?: boolean;
  /**
   * The owner answers the search: the shell always draws a search box, and what is typed goes back in the dry run's body
   * under `search.<key>` (`CHOICE_SEARCH_FIELD`), so the owner can offer options beyond the ones first listed, ones that
   * cannot be picked greyed with their `disabled_reason`. The picked options stay offered whatever is typed. Without it
   * the options are all there is: a long list narrows locally, as `useChoiceSearch` does.
   */
  searchable?: boolean;
}

/** The dry-run body key a searchable choice's typed text travels under: `{ search: { <choice key>: "text" } }`. Never committed. */
export const CHOICE_SEARCH_FIELD = "search";

/** A choice's pick: one value, or the values of a multiple choice. */
export type ChoiceValue = string | string[];

export interface Confirmation {
  key: string;
  label: string;
}

export type DisplayBlock =
  | {
      kind: "file";
      name: string;
      size_bytes: number | null;
      href: string | null;
    }
  | {
      kind: "before_after";
      label: string | null;
      before: string[];
      after: string[];
    }
  | { kind: "facts"; title: string | null; rows: [string, string][] }
  | { kind: "note"; lines: string[] }
  | FiguresBlock
  | TableBlock
  | PendingPricesBlock
  | EmailBlock
  | QuoteBlock;

/**
 * A few figures and the verdict they add up to, drawn under the choices because they read what was picked: a
 * reconciliation of the picks ("Invoices £557.00 · Purchase orders £465.00", "Over by £92.00 (19.8%): needs accepting").
 * `tone` colours the verdict; `lines` follow it (what the change will do to the record).
 */
export interface FiguresBlock {
  kind: "figures";
  figures: [label: string, value: string][];
  verdict: string;
  tone: "ok" | "warn" | "fail" | "neutral";
  lines: string[];
}

/**
 * Rows to compare, as a small table: what a commit found (each supplier's answer from Compare suppliers…,
 * catalogue-management-in-one). Cells are words already formatted; an empty string is an empty cell.
 */
export interface TableBlock {
  kind: "table";
  title: string | null;
  columns: string[];
  rows: string[][];
}

/**
 * Words someone outside the business wrote, or One's summary of them, quoted in
 * a dry run (`QuoteBlock` in `modules/object_contract.py`): `label` is One's
 * words, `text` the quote, shown as written and fenced for Claude.
 */
export interface QuoteBlock {
  kind: "quote";
  label: string;
  text: string;
}

/**
 * An email the command will send, in full, composed by the code that sends it
 * (`EmailBlock` in `modules/object_contract.py`). Text in square brackets is a
 * value only known when it is sent (the order's link). A command that sends
 * several shows the first and counts the rest in `count_more`.
 */
export interface EmailBlock {
  kind: "email";
  to: string[];
  cc: string[];
  subject: string;
  /** Plain text: line breaks kept, never HTML. */
  body: string;
  attachments: string[];
  count_more: number | null;
  /** What the further emails are, after "…and N more like it", e.g. "one to each supplier". */
  count_more_label: string | null;
}

/**
 * Prices still coming (live supplier pricing spec §6.3): the routes to ask again for, and the best route priced so far
 * with the request-body values that choose it (`use`) and record why.
 */
export interface PendingPricesBlock {
  kind: "pending_prices";
  routes: {
    route_code: string;
    route_label: string;
    retry_after_seconds: number;
  }[];
  best_priced: {
    route_label: string;
    price_pence: number;
    use: Record<string, string>;
  } | null;
}

export interface RowOutcome {
  target: ObjectRef;
  label: string | null;
  ok: boolean;
  error: string | null;
}

/** Per-record facts about one request field the schema cannot carry. */
export interface FieldHint {
  /** The address widget's postcode lookup, scoped to this record's customer. */
  lookup_href: string | null;
  /** The nested create for an entity/address field, presented for this record. */
  create: Command | null;
  /**
   * An entity field's starting filters: the named object's list params its
   * picker opens on (the reshape picker's supplier, open to bill and amount
   * window). The person can switch them off to search everything.
   */
  list_filters?: Record<string, string | string[]> | null;
  /**
   * An entity field's fixed scope: list params always applied to its picker, which the person cannot switch off (Set
   * contact… lists only this customer's active users). Unlike `list_filters`, which they can drop.
   */
  list_scope?: Record<string, string | string[]> | null;
  /**
   * The starting filters the person may change, each as a choice of that filter's values drawn above the picker
   * (Add catalogue item…'s Catalogues, starting on the customer's own): the picker searches what is chosen, and
   * everything when nothing is. Without it, starting filters are one "Suggested only" switch.
   */
  list_filter_choices?: ListFilterChoice[] | null;
  /**
   * The words for the value an entity field starts on, when its picker cannot look the value up (the default order
   * contact starts on a person's id, which the picker would otherwise show).
   */
  value_label?: string | null;
  /**
   * The words for each value a many-valued entity field holds, keyed by the value it sends (the contact people on a
   * communication, by person id), so each chip shows a name, not an id.
   */
  value_labels?: Record<string, string> | null;
  /**
   * What a custom input widget needs that is not a body value (the delivery
   * widget's opening deliveries: labels, what has gone, the lock notes).
   */
  widget_context?: Record<string, unknown> | null;
  /** One sentence drawn under the field, worded by the backend ("Goods in: Unit 4, Leicester LE18 2BJ. ..."). */
  note?: string | null;
  /** The field's ghost text for this record, over any the schema gives ("PO-K20444's goods haven't been received yet. Say why it can be paid."). */
  placeholder?: string | null;
  /**
   * Makes an optional field required for this record: no "(optional)" marker, and the shell does not commit while it is
   * empty. The owner still returns a `required` field error, so the server enforces it too.
   */
  required?: boolean | null;
  /**
   * A table the person adds and removes rows of: the row keys to show first (the value of its read-only key column,
   * as text: an invoice's line numbers), the rest behind "Show all". For a long table where only some rows need the
   * person ("4 of 100 lines need a look"). A row with no key yet (one just added) is always shown. Absent or null:
   * every row is shown.
   */
  rows_shown?: string[] | null;
  /**
   * On a keyed-files field: the files this record takes, one picker each, in order (a repeatable item's parts). Each
   * picked file is sent with its slot's key; an error on it names `<field>.<key>`.
   */
  file_slots?: FileSlot[] | null;
  /**
   * The field is not drawn for this record, because filling it would change nothing (the box to also invite an address
   * that is already a portal user). The body keeps whatever value it was prefilled with.
   */
  hidden?: boolean | null;
}

/** One file a keyed-files field takes for this record (`FieldHint.file_slots`). */
export interface FileSlot {
  /** Sent in the field's keys list beside its file ("cover"). */
  key: string;
  /** The slot's words ("Cover"); a lone slot is drawn under the field's own label. */
  label: string;
  /** One line under the slot ("Now cover.pdf"). */
  note?: string | null;
  /** The commit waits until this slot has a file. */
  required?: boolean;
}

export interface DryRun {
  command: CommandId;
  can_commit: boolean;
  effect: string;
  checks: Check[];
  choices: Choice[];
  confirmations: Confirmation[];
  display: DisplayBlock[];
  errors: FieldError[];
  /** Named request fields echoed verbatim into the commit body. */
  guard: Record<string, string | number> | null;
  /** Starting body values for this record (table rows with their max fields, opening deliveries). */
  prefill: Record<string, unknown> | null;
  /** By request-body path. */
  field_hints: Record<string, FieldHint>;
  rows: RowOutcome[] | null;
}

/** A link a command hands back for the person to open or pass on (a card checkout, a supplier's file link), or a timeline event carries (a comment's file). */
export interface CommandLink {
  label: string;
  /** Absolute or external URL. */
  href: string;
  /** ISO datetime the link stops working, for a signed or time-limited link. Absent or null: it does not. */
  expires_at?: string | null;
}

/** What a successful command did not do, or only partly did, that the person should still read. */
export interface CommandWarning {
  /** Stable snake_case identifier, e.g. `order_contact_not_saved`. */
  code: string;
  message: string;
}

/**
 * Where a commit hands the person on to (Ben's ruling, 31 Oct: catalogue-management-in-one): the shell opens `record`,
 * then that record's own `command` with `body` as its starting values. Add to draft quote… on a catalogue item picks the
 * draft quote, then opens its Add catalogue item… with the item picked, so the line is configured on the quote. The
 * command must be one the record offers; if it is not offered or cannot run, the record simply opens.
 */
export interface CommandHandover {
  record: ObjectRef;
  command: CommandId;
  body: Record<string, unknown>;
}

/** Every command's commit response. */
export interface CommandResult {
  summary: string;
  created: ObjectRef | null;
  /** Hand the person on to another record's command (`CommandHandover`); absent or null: none. */
  handover?: CommandHandover | null;
  rows: RowOutcome[] | null;
  /** Links handed back, in order (one card checkout, or one per supplier file). Absent or empty: none. */
  links?: CommandLink[];
  /** What the command did not do though it succeeded. Absent or empty: nothing. */
  warnings?: CommandWarning[];
  /** What the commit found, for the person to read before closing (Compare suppliers…' answers). Absent or empty: none. */
  display?: DisplayBlock[];
}

/** Platform fields on a command body, handled by name, never rendered. */
export const PLATFORM_BODY_FIELDS = ["request_id", "confirmed"] as const;

/** A bulk command's body field carrying the selected records' identifiers. */
export const BULK_TARGETS_FIELD = "targets";

/** A file chosen in a file widget, carried in a body until the multipart encode. */
/** Where an upload field's file goes before the command runs (`cis_field(upload=UploadTarget(...))`). */
export interface UploadTarget {
  /** `POST`, multipart. */
  path: string;
  /** The form part the file is sent in. */
  file_field: string;
  /** The key of the JSON answer holding the ref the field takes. */
  ref_field: string;
}

export interface ChosenFile {
  kind: "chosen_file";
  name: string;
  size: number;
  blob: Blob;
}

export function isChosenFile(value: unknown): value is ChosenFile {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { kind?: unknown }).kind === "chosen_file"
  );
}
